// filepath: apps/web/src/design-system/ConflictResolution/ConflictResolution.tsx
//
// FR-UI-CORE-6 — Conflict-resolution modal UI.
//
// Purpose:
//   Surfaces the three required resolution actions (ARCHITECTURE_WEB.md §5):
//     1. "Re-apply my changes onto current version"   → reapply
//     2. "Refetch latest"                              → refetch
//     3. "Discard my changes"                          → discard
//
//   plus a dismissible read-only diff view of the server's
//   `currentRecord` vs the user's pending `userRecord` so the user
//   can see what diverged (AC-2).
//
// CONTRACT (FR-UI-CORE-6):
//   - This component is controlled by `useConflictResolver()`. The
//     caller (FormRenderer, future TaskInbox) mounts it once; it
//     renders nothing when no conflict is active.
//   - Clicking an action button calls `onResolve(action)` where
//     `action ∈ { 'reapply' | 'refetch' | 'discard' }`. The caller
//     decides what to do with the result (e.g. re-PUT for 'reapply',
//     invalidate the cache for 'refetch', reset the form for
//     'discard').
//   - Clicking the close / cancel control or pressing Escape calls
//     `onDismiss()`. The user has explicitly walked away — the SPA
//     must NOT retry the write (AC-4: no silent overwrite).
//
// INVARIANTS:
//   - INV-9: server-supplied strings are rendered as text nodes;
//     `dangerouslySetInnerHTML` is never used. The static-guard
//     regression net at apps/web/src/runtime/staticGuards.test.ts
//     enforces this whole-tree.
//   - INV-2 / FR-AUTH-7: `currentRecord` is the authoritative shape.
//     Fields stripped by the server are not shown and not merged back
//     from `userRecord` (delegated to the resolver hook).
//   - The component is portal-mounted to avoid z-index clipping by
//     deeply nested field components (per the impact analysis's
//     "modal preferred" recommendation).
//   - The component does NOT carry tenant-authored HTML through any
//     unsafe path — the only user-visible strings are i18n chrome
//     (Russian by default per FR-UI-CORE-8).

import React from "react";
import { createPortal } from "react-dom";
import {
  useConflictResolver,
  type ConflictAction,
} from "@/renderers/__shared__/useConflictResolver";
import { useTranslation } from "@/i18n/useTranslation";
import { focusTrap } from "@/runtime/focusTrap";

export interface ConflictResolutionProps {
  /**
   * Called when the user picks a resolution action. The caller is
   * responsible for routing the action to the appropriate handler
   * (e.g. re-PUT for 'reapply', invalidate cache for 'refetch',
   * reset local form state for 'discard').
   */
  onResolve: (action: ConflictAction) => void;
  /**
   * Called when the user dismisses the modal without picking an
   * action. The caller MUST NOT retry the original write with the
   * stale `row_version` (AC-4).
   */
  onDismiss?: () => void;
}

export function ConflictResolution({
  onResolve,
  onDismiss,
}: ConflictResolutionProps): React.ReactElement | null {
  const resolver = useConflictResolver();
  const { t } = useTranslation();

  // The active conflict payload (or null when no conflict is active).
  // Cached at the top of the function so all hooks + handlers below
  // can reference it consistently. A Rules-of-Hooks-safe pattern:
  // hooks are called UNCONDITIONALLY here; the conditional logic
  // lives inside the useEffect body and the early-return.
  const conflict = resolver.conflict;

  function handleDismiss(): void {
    resolver.dismiss();
    onDismiss?.();
  }

  function handleResolve(action: ConflictAction): void {
    resolver.resolve(action);
    onResolve(action);
  }

  // Escape-key handler — dismisses the modal without resolving.
  // Rules of Hooks: this useEffect MUST be called on every render
  // (unconditional) so React can match hook order across renders. We
  // attach the listener only when a conflict is active; the cleanup
  // function removes it on conflict change or unmount.
  //
  // FR-UI-CORE-9: the focus trap installed below also dispatches a
  // `cancel` CustomEvent on the container when Escape is pressed. We
  // listen for that event here too (capture phase) so the trap's
  // stopPropagation does not swallow the dismiss intent. The
  // keydown listener remains as a safety net for any code path
  // that activates the dialog without the focus trap.
  React.useEffect(() => {
    if (conflict === null) return undefined;
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        handleDismiss();
      }
    }
    function onCancelEvent(e: Event): void {
      e.preventDefault();
      handleDismiss();
    }
    document.addEventListener("keydown", onKey);
    const container = containerRef.current;
    if (container !== null) {
      container.addEventListener("cancel", onCancelEvent as EventListener);
    }
    return () => {
      document.removeEventListener("keydown", onKey);
      if (container !== null) {
        container.removeEventListener("cancel", onCancelEvent as EventListener);
      }
    };
  }, [conflict]);

  // FR-UI-CORE-9: focus trap on the active dialog. The teardown
  // function is stable across re-renders (we only call it when the
  // conflict transitions to null), so the dependency array tracks
  // the conflict value directly. Restoring focus to the previously
  // focused element is handled by focusTrap() itself.
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (conflict === null) return undefined;
    const el = containerRef.current;
    if (el === null) return undefined;
    // Autofocus the close button (safer default than the
    // destructive action buttons).
    const closeButton = el.querySelector<HTMLButtonElement>(
      "button.conflict-resolution-close",
    );
    const teardown = focusTrap(el, { initialFocus: closeButton ?? null });
    return teardown;
  }, [conflict]);

  // Render nothing when no conflict is active.
  if (conflict === null) return null;

  // Portal-mount to avoid z-index / clipping issues with deeply
  // nested field components.
  if (typeof document === "undefined") return null;
  return createPortal(
    <div
      ref={containerRef}
      className="conflict-resolution-overlay"
      data-testid="conflict-resolution"
      role="dialog"
      aria-modal="true"
      aria-labelledby="conflict-title"
      aria-describedby="conflict-body"
    >
      <div
        className="conflict-resolution-modal"
        data-testid="conflict-resolution-modal"
      >
        <header className="conflict-resolution-header">
          <h2 id="conflict-title" data-testid="conflict-title">
            {t("conflict.title")}
          </h2>
          <button
            type="button"
            className="conflict-resolution-close"
            data-testid="conflict-close"
            aria-label="Close"
            onClick={handleDismiss}
          >
            ×
          </button>
        </header>
        <p className="conflict-resolution-body" data-testid="conflict-body">
          {t("conflict.body")}
        </p>

        <ConflictDiff
          currentRecord={conflict.currentRecord}
          userRecord={conflict.userRecord}
          currentRowVersion={conflict.currentRowVersion}
        />

        <div
          className="conflict-resolution-actions"
          data-testid="conflict-actions"
        >
          <button
            type="button"
            className="conflict-action-reapply"
            data-testid="conflict-action-reapply"
            onClick={() => handleResolve("reapply")}
          >
            {t("conflict.reapply")}
          </button>
          <button
            type="button"
            className="conflict-action-refetch"
            data-testid="conflict-action-refetch"
            onClick={() => handleResolve("refetch")}
          >
            {t("conflict.refetch")}
          </button>
          <button
            type="button"
            className="conflict-action-discard"
            data-testid="conflict-action-discard"
            onClick={() => handleResolve("discard")}
          >
            {t("conflict.discard")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Read-only diff view. Renders `currentRecord` and `userRecord`
 * side-by-side as text nodes (INV-9 — no `dangerouslySetInnerHTML`).
 * When `currentRecord` is null (server omitted it), only the user's
 * pending edit is shown and the "Refetch latest" action is the only
 * path that produces a sensible result.
 */
function ConflictDiff({
  currentRecord,
  userRecord,
  currentRowVersion,
}: {
  currentRecord: Record<string, unknown> | null;
  userRecord: Record<string, unknown>;
  currentRowVersion: number | null;
}): React.ReactElement {
  return (
    <div className="conflict-diff" data-testid="conflict-diff">
      <section
        className="conflict-diff-current"
        data-testid="conflict-diff-current"
      >
        <h3>Server (current)</h3>
        {currentRowVersion !== null ? (
          <div
            className="conflict-diff-row-version"
            data-testid="conflict-row-version"
          >
            row_version: <code>{currentRowVersion}</code>
          </div>
        ) : null}
        {currentRecord === null ? (
          <div
            className="conflict-diff-empty"
            data-testid="conflict-diff-empty"
          >
            (no diff context — server omitted current record body)
          </div>
        ) : (
          <pre
            className="conflict-diff-pre"
            data-testid="conflict-diff-current-pre"
          >
            {JSON.stringify(currentRecord, null, 2)}
          </pre>
        )}
      </section>
      <section className="conflict-diff-user" data-testid="conflict-diff-user">
        <h3>Your changes</h3>
        <pre className="conflict-diff-pre" data-testid="conflict-diff-user-pre">
          {JSON.stringify(userRecord, null, 2)}
        </pre>
      </section>
    </div>
  );
}
